import React, { useState, useCallback } from "react";
import {
  Box,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Text,
  Button,
  Layer,
  Heading,
  Notification,
} from "grommet";
import { View, Archive, Upload } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import StatusDot from "./StatusDot";

// const SkillDot = ({ status }) => {
//   const color = status === 0 ? "status-critical" : "status-ok";
//   return <Box width="10px" height="10px" round="full" background={color} />;
// };

const ActionButton = ({ skill, onUnpublish, onPublish }) => {
  const handleClick = useCallback(() => {
    if (skill.status === 1) {
      onUnpublish(skill);
    } else {
      onPublish(skill);
    }
  }, [skill, onUnpublish, onPublish]);

  const icon =
    skill.status === 1 ? (
      <Archive color="status-critical" />
    ) : (
      <Upload color="brand" />
    );
  const title = skill.status === 1 ? "Unpublish skill" : "Publish skill";

  return (
    <Button title={title} icon={icon} hoverIndicator onClick={handleClick} />
  );
};

const SkillQuestionMappingTable = ({
  skillData = [],
  onEdit,
  portfolio_id,
}) => {
  const navigate = useNavigate();

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // "unpublish" or "publish"
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [notification, setNotification] = useState(null);

  const sortedskillData = [...skillData].sort((a, b) => a.status - b.status);

  const openUnpublishConfirm = (skill) => {
    setSelectedSkill(skill);
    setConfirmType("unpublish");
    setConfirmVisible(true);
  };

  const openPublishConfirm = (skill) => {
    setSelectedSkill(skill);
    setConfirmType("publish");
    setConfirmVisible(true);
  };

  const confirmAction = async () => {
    if (!selectedSkill || !confirmType) return;

    try {
      const url =
        confirmType === "unpublish"
          ? `/api/admin/unpublish-skill/${selectedSkill.skillId}`
          : `/api/admin/publish-skill/${selectedSkill.skillId}`;
      await axios.patch(url);

      // notify parent of status change
      if (onEdit) {
        onEdit({
          ...selectedSkill,
          status: confirmType === "publish" ? 1 : 0,
        });
      }

      setNotification({
        status: "normal",
        message:
          confirmType === "unpublish"
            ? "Skill unpublished successfully"
            : "Skill published successfully",
      });
      setTimeout(() => window.location.reload(), 2500);
    } catch (err) {
      setNotification({
        status: "critical",
        message:
          err.response?.data?.message || err.message || "Something went wrong",
      });
    } finally {
      setConfirmVisible(false);
      setSelectedSkill(null);
      setConfirmType(null);
    }
  };

  return (
    <Box overflow="auto" background="light-1" round="small" pad="small">
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell rowSpan="2">
              <Text weight="bold">Skill Name</Text>
            </TableCell>
            <TableCell colSpan="3" align="center">
              <Text weight="bold">Questions Distribution</Text>
            </TableCell>
            <TableCell rowSpan="2" align="center">
              <Text weight="bold">Courses</Text>
            </TableCell>
            <TableCell rowSpan="2" align="center">
              <Text weight="bold">Actions</Text>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Text weight="bold">Beginner</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Intermediate</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Total</Text>
            </TableCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {skillData.length === 0 ? (
            <TableRow>
              <TableCell colSpan="6" align="center">
                <Text>No skill data found.</Text>
              </TableCell>
            </TableRow>
          ) : (
            sortedskillData.map((skill, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Box direction="row" align="center" gap="small">
                    <Box
                      title={skill.status === 1 ? "Published" : "Unpublished"}
                    >
                      <StatusDot status={skill.status} />
                    </Box>
                    <Text>{skill.skillName}</Text>
                  </Box>
                </TableCell>
                <TableCell>{skill.beginner}</TableCell>
                <TableCell>{skill.intermediate}</TableCell>
                <TableCell>{skill.total}</TableCell>
                <TableCell align="center">{skill.courses}</TableCell>
                <TableCell align="center">
                  <Box direction="row" gap="xxsmall">
                    <Button
                      icon={<View color="brand" />}
                      title="View questions"
                      hoverIndicator
                      onClick={() =>
                        navigate(
                          `/admin/portfolio/${portfolio_id}/skill-question/${skill.skillId}`,
                        )
                      }
                    />
                    <ActionButton
                      skill={skill}
                      onUnpublish={openUnpublishConfirm}
                      onPublish={openPublishConfirm}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {confirmVisible && selectedSkill && (
        <Layer
          onEsc={() => setConfirmVisible(false)}
          onClickOutside={() => setConfirmVisible(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">
              {confirmType === "unpublish"
                ? "Unpublish Skill"
                : "Publish Skill"}
            </Heading>
            <Text>
              Are you sure you want to{" "}
              <strong>
                {confirmType === "unpublish" ? "unpublish" : "publish"}
              </strong>{" "}
              <em>{selectedSkill.skillName}</em>?
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button label="No" onClick={() => setConfirmVisible(false)} />
              <Button label="Yes" primary onClick={confirmAction} />
            </Box>
          </Box>
        </Layer>
      )}

      {notification && (
        <Notification
          toast
          status={notification.status}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </Box>
  );
};

export default SkillQuestionMappingTable;
